import React, { useEffect, useMemo, useRef } from 'react'
import {
  Chart,
  LineElement,
  PointElement,
  ScatterController,
  LinearScale,
  Decimation,
  Filler,
  Title
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { getRelativePosition } from 'chart.js/helpers'
import { useSettings } from '../slices/settings'
import { distance, inverseDistance, inverseAltitude, altitude, travelTime } from '../utils'

// ignora la dimensione dei punti nel calcolare il padding
PointElement.prototype.size = () => 0

const RED = '#B22222'
const GRAY = 'rgba(210, 210, 210, .7)'
const WHITE = '#FFFFFF'
const BLUE = '#499BC8'

export const findNearestInProfile = (profile, value, key) =>{
  key = key || 'x'
  let mid;
  let lo = 0;
  let hi = profile.length - 1;
  while (hi - lo > 1) {
    mid = Math.floor ((lo + hi) / 2);
    if (profile[mid][key] < value) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  if (value - profile[lo][key] <= profile[hi][key] - value) {
    return profile[lo];
  }
  return profile[hi];
}


Chart.register(
  PointElement,
  LineElement,
  ScatterController,
  LinearScale,
  Filler,
  Decimation,
  ChartDataLabels,
  Title
)

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {},
  animation: {},
  tension: 1,
  events: [],
  scales: {
    x: {
      type: 'linear',
      min: 0,
      scaleLabel: {
        display: false
      },
      grid: {
        drawBorder: true,
        drawOnChartArea: true
      },
      ticks: {
        padding: 0,
        precision: 0,
        showLabelBackdrop: false
      },
      afterFit: function (ax) {
        ax.paddingRight = 15
        ax.paddingLeft = 15
      }
    },
    y: {
      type: 'linear',
      ticks: {},
      grid: {},
      suggestedMin: 0,
    }
  },
  plugins: {
    title: {
      display: true,
      color: BLUE,
      fullSize: false,
      padding: 10,
      text: '-- / -- / --'
    },
    decimation: {
      algorithm: 'lttb',
      enabled: true,
      samples: 10
    }
  }

}

const pinOptions = {
  data: [],
  showLine: false,
  backgroundColor: WHITE,
  borderColor: BLUE,
  borderWidth: 5,
  pointRadius: 6,
  clip: 20,
  datalabels: {
    display: false
  }
}

const userPositionOptions = {
  data: [],
  showLine: false,
  backgroundColor: BLUE,
  borderColor: WHITE,
  borderWidth: 3,
  pointRadius: 6,
  clip: 20,
  datalabels: {
    display: false
  }
}

const markersOptions = {
  data: [],
  showLine: false,
  backgroundColor: WHITE,
  borderColor: RED,
  borderWidth: 1.5,
  pointRadius: 3.5,
  clip: 20,
  datalabels: {
    display: 'auto',
    rotation: -90,
    offset: 10,
    anchor: 'start',
    clip: true,
    font: {
      size: 14
    },
    textAlign: (context) => {
      const index = context.dataIndex
      const value = context.dataset.data[index]
      const yScale = context.chart.scales.y
      const ystep = (yScale.max * 0.6)
      if (value.y > ystep) {
        return 'end'
      } else {
        return 'start'
      }
    },
    align: (context) => {
      const index = context.dataIndex
      const value = context.dataset.data[index]
      const xScale = context.chart.scales.x
      const yScale = context.chart.scales.y
      const ystep = (yScale.max * 0.6)
      const xstep = (xScale.max / 12)

      if (value.x < xstep) {
        if (value.y > ystep) {
          return 65
        } else {
          return -65
        }
      }

      if (value.x > (xScale.max - xstep)) {
        if (value.y > ystep) {
          return -240
        } else {
          return -120
        }
      }

      if (value.y > ystep) {
        return 'start'
      }

      return 'end'
    },

    formatter: (value, context) => {
      return value.label
    }
  }
}

const profileOptions = {
  data: [],
  fill: {
    target: 'start',
    above: GRAY
  },
  showLine: true,
  spanGaps: true,
  borderColor: RED,
  borderWidth: 3,
  pointRadius: 0,
  datalabels: {
    display: false
  },
  plugins: {
    decimation: {
      algorithm: 'lttb',
      enabled: true,
      samples: 10
    }
  },
  size: 0
}

const Profile = (props) => {

  const { compact, profile, markers, pin, unit, speed, suggestedMax, userPosition} = props
  const { onClick, onMouseOut, onMouseMove } = props
  const { zoom } = useSettings()
  const container = useRef()
  const chart = useRef()
  const offsetDistance = useRef(distance(userPosition && userPosition[0], unit)[0])
  const maxValue = profile && profile[profile.length - 1]

  useEffect(() => {
    const getDatasets = () => {
      if (!markers || !profile) return []

      const datasets = []
      const pinDataset = Object.assign({}, pinOptions)
      const userPositionDataset = Object.assign({}, userPositionOptions)
      const profileDataset = Object.assign({}, profileOptions)
      const markersDataset = Object.assign({}, markersOptions)

      datasets.push(pinDataset)
      datasets.push(userPositionDataset)

      markersDataset.datalabels.display = compact ? false : 'auto'
      markersDataset.data = markers.map(x => ({
        x: distance(x.distance, unit)[0],
        y: altitude(x.elevation, unit)[0],
        label: x.label
      }))
      datasets.push(markersDataset)

      profileDataset.data = profile.map(x => ({
        x: distance(x.distance, unit)[0],
        y: altitude(x.elevation, unit)[0],
        time: x.time,
        lnglat: x.lnglat
      }))
      datasets.push(profileDataset)
      return datasets
    }


    const getOptions = () => {
      const opt = Object.assign({}, chartOptions)
      opt.scales.x.min = 0
      opt.scales.x.max = maxValue && distance(maxValue.distance, unit)[0]
      opt.scales.x.suggestedMax = suggestedMax && distance(suggestedMax[0], unit)[0]
      opt.scales.x.grid.drawBorder = !compact
      opt.scales.x.grid.zeroLineWidth = compact ? 0 : 1
      opt.scales.x.ticks.includeBounds = true
      opt.scales.x.ticks.callback = function(value, index, values) {
        const offsetValue = Math.abs(value - offsetDistance.current)
        return offsetValue.toFixed(1);
      }
      opt.scales.x.afterBuildTicks = axis => {
        if (markers && maxValue) {
          if (!compact){
            const margin = maxValue.distance / axis.width * 30
            const ticks = []
            for (const m of markers){
              const near = ticks.length > 0 ? ticks.reduce((a, b) => Math.abs(m.distance - b.distance) < Math.abs(m.distance - a.distance) ? b : a) : null
              if (!near || Math.abs(m.distance - near.distance) > margin ) ticks.push(m)
            }
            axis.ticks = ticks.map(v => ({ value: distance(v.distance, unit)[0] }))
          }
          axis.ticks = axis.ticks.filter(v => v.value > 0 )
        }
      }
      opt.scales.y.grid.drawTicks = !compact
      opt.scales.y.grid.drawBorder = !compact
      opt.scales.y.grid.zeroLineWidth = compact ? 0 : 1
      opt.scales.y.suggestedMax = suggestedMax && altitude(suggestedMax[1], unit)[0]
      opt.scales.y.ticks.display = !compact
      //opt.animation.duration = compact ? 0 : 1000
      opt.layout.padding = compact
        ? 0
        : {
            top: 0,
            left: 5,
            bottom: 5,
            right: 10
          }
      opt.plugins.title.text = '-- / -- / --'
      return opt
    }

    const fixCompactLayout = {
      id: 'fixlayout',
      beforeDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d')
        ctx.save()
        ctx.globalCompositeOperation = 'destination-over'
        const xScale = chart.scales.x
        const yScale = chart.scales.y
        const left = xScale.getPixelForValue(xScale.min)
        const right = xScale.getPixelForValue(xScale.max)
        const bottom = yScale.getPixelForValue(yScale.min)
        const profile = chart.data.datasets[3]

        ctx.fillStyle = GRAY
        ctx.fillRect(0, bottom, chart.width, chart.height - bottom)

        if (profile && profile.data && profile.data.length > 2) {
          chart.ctx.fillRect(0, bottom, left, yScale.getPixelForValue(profile.data[0].y) - bottom)
          chart.ctx.fillRect(right, bottom, chart.width - right,
            yScale.getPixelForValue(profile.data[profile.data.length - 1].y) - bottom)
        }
        ctx.restore()
      }
    }

    if (!chart.current) {
      Chart.defaults.font.family = 'Barlow Condensed'
      Chart.defaults.font.size = 14 * zoom
      Chart.defaults.font.color = '#777777'
      chart.current = new Chart(container.current, {
        data: { 
          datasets: getDatasets() 
        },
        type: 'scatter',
        plugins: compact ? [fixCompactLayout] : null,
        options: getOptions()
      })
    } else {
      if (!markers && !maxValue) return
      Chart.defaults.font.size = 14 * zoom
      chart.current.options = getOptions()
      chart.current.config.data.datasets = getDatasets()
      chart.current.update()
    }
  }, [maxValue, markers, compact, unit, zoom, suggestedMax])

  useEffect(() => {
    return () => {
      chart.current.destroy()
    }
  }, [])

  useEffect(() => {
    offsetDistance.current = distance(userPosition && userPosition[0], unit)[0]
    if (!chart.current) return
    if (chart.current.data.datasets[0]){
      chart.current.data.datasets[0].data.pop()
    }
    if (chart.current.data.datasets[1]){
      chart.current.data.datasets[1].data.pop()
    }
    chart.current.options.plugins.title.text = '-- / -- / --'

    if (!maxValue) return

    if (pin) {
      const [dist, distSuffix] =  distance(pin[0], unit)
      const [alt, altSuffix] =  altitude(pin[1], unit)
      chart.current.data.datasets[0].data.push({ x: dist, y: alt })
      const offsetValue = findNearestInProfile(chart.current.data.datasets[3].data, offsetDistance.current)
      const time = travelTime(Math.abs(pin[2] - offsetValue.time), speed)
      const relativeDistance = Math.abs(dist - offsetValue.x)
      chart.current.options.plugins.title.text = `${relativeDistance.toFixed(1)} ${distSuffix} / ${time} / ${alt} ${altSuffix}`
    }   

    if (userPosition){
        const [dist, distSuffix] =  distance(userPosition[0], unit)
        const [alt, altSuffix] =  altitude(userPosition[1], unit)
        chart.current.data.datasets[1].data.push({ x: dist, y: alt })
        if (!pin){
          const time = travelTime(maxValue.time - userPosition[2], speed)
          const relativeDistance = distance(maxValue.distance, unit)[0] - dist
          chart.current.options.plugins.title.text = `${relativeDistance.toFixed(1)} ${distSuffix} / ${time} / ${alt} ${altSuffix}`
        }
    }

    chart.current.update('none')
  }, [maxValue, pin, unit, speed, userPosition])

  return useMemo(() => {

    const withProfileEvent = (fn) => {
      return function(e) {
        if (!chart.current) return
        const canvasPosition = getRelativePosition(e, chart.current)
        const x = chart.current.scales.x.getValueForPixel(canvasPosition.x)
        const point = findNearestInProfile(chart.current.data.datasets[3].data, x)
        const evt = {
          distance: inverseDistance(point.x, unit),
          elevation: inverseAltitude(point.y, unit),
          lnglat: point.lnglat,
          time: point.time,
          originalEvent: e
        }
        fn(evt)
      }
    }

    return (
      <canvas ref={container}
        onClick={onClick ? withProfileEvent(onClick): null}
        onMouseMove={onMouseMove ? withProfileEvent(onMouseMove): null}
        onMouseOut={onMouseOut ? withProfileEvent(onMouseOut): null} >
      </canvas>
    )
  }, [onClick, onMouseMove, onMouseOut, unit])

}

export default Profile
